import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { LineChart, X, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import config from '../../../config';

// ---------------------------------------------------------------------------
// PatientHealthButton
//
// A small graph icon that sits next to PatientReportsButton. Clicking it
// opens a popup with everything the patient's wearable has recorded:
//   - automated summary (Regulation / Recovery / Daily Integration status,
//     plus the positives / watch-outs the backend already computes)
//   - one chart per metric (heart rate, HRV, blood pressure, respiratory
//     rate, skin temperature, movement, active energy)
//   - a day-by-day table of every reading
//
// Date range: 7 days / 1 month / 3 months quick chips, plus Custom
// (exact From / To dates, or "last N days / months / years", up to 30 years).
// Long ranges are averaged into weekly / monthly points so charts stay fast.
//
// Data comes from GET /api/doctor-portal/patients/:patientId/health?days=N,
// which checks the doctor actually has a confirmed/completed appointment with
// this patient before returning anything. The server must allow `days` up to
// MAX_RANGE_YEARS * 366 for the long ranges to return data.
//
// The popup is rendered through a portal into document.body, and its
// clicks are stopped from bubbling, because this button lives inside
// AppointmentListItem's own <button onClick> — without that, clicking
// inside the popup would also "select" the appointment behind it.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Date range config (same rules as the patient Health page)
// ---------------------------------------------------------------------------

const MAX_RANGE_YEARS = 30;

const RANGE_PRESETS = [
  { id: '7d', label: '7 days', unit: 'days', amount: 7 },
  { id: '1m', label: '1 month', unit: 'months', amount: 1 },
  { id: '3m', label: '3 months', unit: 'months', amount: 3 },
];

const CUSTOM_RANGE_ID = 'custom';
const QUICK_UNITS = ['days', 'months', 'years'];

// Rows rendered per "page" in the daily table.
const LIST_PAGE_SIZE = 100;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Local-time YYYY-MM-DD (toISOString would shift the day for UTC+05:30 etc.)
function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseISODate(iso) {
  return new Date(`${String(iso).slice(0, 10)}T00:00:00`);
}

function daysBetweenInclusive(fromIso, toIso) {
  return Math.round((parseISODate(toIso) - parseISODate(fromIso)) / MS_PER_DAY) + 1;
}

function subtractFromDate(base, unit, amount) {
  const d = new Date(base);
  if (unit === 'days') d.setDate(d.getDate() - (amount - 1)); // "7 days" includes today
  if (unit === 'months') d.setMonth(d.getMonth() - amount);
  if (unit === 'years') d.setFullYear(d.getFullYear() - amount);
  return d;
}

function earliestAllowedDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MAX_RANGE_YEARS);
  return toISODate(d);
}

// { from, to, days (back from today, sent to the API), totalDays (in range) }
function buildRange(selection) {
  const todayIso = toISODate(new Date());
  const floorIso = earliestAllowedDate();

  let from;
  let to = todayIso;

  if (selection.id === CUSTOM_RANGE_ID) {
    from = selection.from || todayIso;
    to = selection.to || todayIso;
  } else {
    const preset = RANGE_PRESETS.find(p => p.id === selection.id) || RANGE_PRESETS[0];
    from = toISODate(subtractFromDate(new Date(), preset.unit, preset.amount));
  }

  if (from < floorIso) from = floorIso;
  if (to > todayIso) to = todayIso;
  if (from > to) from = to;

  return {
    from,
    to,
    days: daysBetweenInclusive(from, todayIso),
    totalDays: daysBetweenInclusive(from, to),
  };
}

// Day / week / month buckets so a 30-year range stays readable and fast.
function pickGranularity(totalDays) {
  if (totalDays <= 120) return 'day';
  if (totalDays <= 1100) return 'week';
  return 'month';
}

const GRANULARITY_LABELS = { day: 'Daily', week: 'Weekly average', month: 'Monthly average' };

function bucketStart(iso, granularity) {
  const d = parseISODate(iso);
  if (granularity === 'week') {
    const dow = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - dow);
    return toISODate(d);
  }
  if (granularity === 'month') {
    return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
  }
  return String(iso).slice(0, 10);
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

// One entry per chart. `series` has 2 entries only for blood pressure.
const METRICS = [
  { id: 'heart_rate', label: 'Heart rate', unit: 'bpm', decimals: 0, series: [{ key: 'heart_rate', color: '#D85A30' }] },
  { id: 'hrv', label: 'HRV', unit: 'ms', decimals: 0, series: [{ key: 'hrv', color: '#8B5CF6' }] },
  {
    id: 'blood_pressure',
    label: 'Blood pressure',
    unit: 'mmHg',
    decimals: 0,
    series: [
      { key: 'blood_pressure', color: '#B3261E', label: 'Systolic' },
      { key: 'blood_pressure_diastolic', color: '#E58F8A', label: 'Diastolic' },
    ],
  },
  { id: 'respiratory_rate', label: 'Respiratory rate', unit: 'br/min', decimals: 1, series: [{ key: 'respiratory_rate', color: '#2A9D8F' }] },
  { id: 'skin_temperature', label: 'Skin temperature', unit: '°C', decimals: 1, series: [{ key: 'skin_temperature', color: '#B4780A' }] },
  { id: 'movement', label: 'Movement', unit: 'steps', decimals: 0, series: [{ key: 'movement', color: '#0F4C75' }] },
  { id: 'active_energy', label: 'Active energy', unit: 'kcal', decimals: 0, series: [{ key: 'active_energy', color: '#2E8B57' }] },
];

const AGGREGATE_KEYS = METRICS.flatMap(m => m.series.map(s => s.key));

const TRAJECTORY_COLORS = {
  stable: '#0F4C75',
  optimal: '#0F6E56',
  improving: '#0F6E56',
  strong: '#0F6E56',
  draining: '#B3261E',
  declining: '#B3261E',
  low: '#B3261E',
  overloaded: '#B3261E',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Number(null) is 0, which would silently turn a missing reading into a
// real zero — so missing values become NaN instead.
const num = v => (v === null || v === undefined || v === '' ? NaN : Number(v));
const isNum = Number.isFinite;

function fmt(v, decimals = 0) {
  const n = num(v);
  if (!isNum(n)) return '--';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

// Adds the year when the range is long enough that "Oct 21" would be ambiguous.
function shortDate(d, withYear = false) {
  if (!d) return '';
  const opts = withYear
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' };
  return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', opts);
}

function trajectoryColor(value) {
  return TRAJECTORY_COLORS[String(value).toLowerCase()] || '#5F5E5A';
}

function trajectoryLabel(value) {
  if (!value) return '--';
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Averages every metric inside each bucket (missing values are ignored).
function aggregateRows(rows, granularity) {
  if (granularity === 'day') return rows;

  const buckets = new Map();

  rows.forEach(row => {
    const key = bucketStart(row.date, granularity);
    if (!buckets.has(key)) buckets.set(key, { date: key, sums: {}, counts: {} });
    const b = buckets.get(key);

    AGGREGATE_KEYS.forEach(k => {
      const v = num(row[k]);
      if (isNum(v)) {
        b.sums[k] = (b.sums[k] || 0) + v;
        b.counts[k] = (b.counts[k] || 0) + 1;
      }
    });
  });

  return Array.from(buckets.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(b => {
      const out = { date: b.date };
      AGGREGATE_KEYS.forEach(k => {
        out[k] = b.counts[k] ? Number((b.sums[k] / b.counts[k]).toFixed(2)) : null;
      });
      return out;
    });
}

async function fetchPatientHealth(token, patientId, days) {
  const res = await fetch(`${config.baseURL}/api/doctor-portal/patients/${patientId}/health?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || body.message || `Request failed (${res.status})`);
  return body.data; // { rows, summary, days }
}

// ---------------------------------------------------------------------------
// Range selector — preset chips + Custom panel
// ---------------------------------------------------------------------------

const rangeInputStyle = {
  padding: '6px 9px',
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  fontSize: 12.5,
  background: '#fff',
};

function RangeSelector({ selection, range, onChange }) {
  const todayIso = toISODate(new Date());
  const minIso = earliestAllowedDate();

  const [draftFrom, setDraftFrom] = useState(range.from);
  const [draftTo, setDraftTo] = useState(range.to);
  const [quickAmount, setQuickAmount] = useState('6');
  const [quickUnit, setQuickUnit] = useState('months');
  const [rangeError, setRangeError] = useState(null);

  const isCustom = selection.id === CUSTOM_RANGE_ID;

  // Keep the date inputs in step when a preset (or quick range) is applied.
  useEffect(() => {
    setDraftFrom(range.from);
    setDraftTo(range.to);
  }, [range.from, range.to]);

  function applyCustom(from, to) {
    if (!from || !to) return setRangeError('Pick both a start and an end date.');
    if (from > to) return setRangeError('Start date must be on or before the end date.');
    if (from < minIso) return setRangeError(`Start date can't be more than ${MAX_RANGE_YEARS} years ago.`);
    if (to > todayIso) return setRangeError("End date can't be in the future.");
    setRangeError(null);
    onChange({ id: CUSTOM_RANGE_ID, from, to });
  }

  function applyQuick() {
    const amount = Math.floor(Number(quickAmount));
    if (!Number.isFinite(amount) || amount < 1) return setRangeError('Enter a number of 1 or more.');
    const from = toISODate(subtractFromDate(new Date(), quickUnit, amount));
    if (from < minIso) return setRangeError(`The maximum range is ${MAX_RANGE_YEARS} years.`);
    applyCustom(from, todayIso);
  }

  const chip = active => ({
    padding: '5px 11px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: `1px solid ${active ? '#0F4C75' : '#E1E4E8'}`,
    background: active ? '#E6F1FB' : 'white',
    color: active ? '#0F4C75' : '#5F5E5A',
  });

  return (
    <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
      <div role="tablist" aria-label="Time range" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {RANGE_PRESETS.map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => { setRangeError(null); onChange({ id: p.id }); }}
            style={chip(selection.id === p.id)}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange({ id: CUSTOM_RANGE_ID, from: range.from, to: range.to })}
          style={chip(isCustom)}
        >
          Custom
        </button>
      </div>

      {isCustom && (
        <div style={{ border: '1px solid #EEF0F2', borderRadius: 10, padding: 12, display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#777', marginBottom: 4 }}>From</label>
              <input
                type="date"
                value={draftFrom}
                min={minIso}
                max={draftTo || todayIso}
                onChange={e => setDraftFrom(e.target.value)}
                style={rangeInputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: '#777', marginBottom: 4 }}>To</label>
              <input
                type="date"
                value={draftTo}
                min={draftFrom || minIso}
                max={todayIso}
                onChange={e => setDraftTo(e.target.value)}
                style={rangeInputStyle}
              />
            </div>
            <button
              type="button"
              onClick={() => applyCustom(draftFrom, draftTo)}
              style={{ padding: '7px 13px', borderRadius: 8, border: 'none', background: '#0F4C75', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
            >
              Apply dates
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#777' }}>or show the last</span>
            <input
              type="number"
              min={1}
              value={quickAmount}
              onChange={e => setQuickAmount(e.target.value)}
              style={{ ...rangeInputStyle, width: 64 }}
              aria-label="Amount"
            />
            <select value={quickUnit} onChange={e => setQuickUnit(e.target.value)} style={rangeInputStyle} aria-label="Unit">
              {QUICK_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <button
              type="button"
              onClick={applyQuick}
              style={{ padding: '7px 13px', borderRadius: 8, border: '1px solid #0F4C75', background: '#fff', color: '#0F4C75', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
            >
              Apply
            </button>
          </div>

          {rangeError && <span style={{ fontSize: 12, color: '#B3261E' }}>{rangeError}</span>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One chart card (hand-rolled SVG — no chart library needed)
// ---------------------------------------------------------------------------

function MetricChart({ metric, rows, trend, withYear }) {
  const W = 300;
  const H = 90;
  const PX = 6;
  const PY = 10;

  const values = metric.series.flatMap(s => rows.map(r => num(r[s.key])).filter(isNum));
  const primary = rows.map(r => num(r[metric.series[0].key])).filter(isNum);

  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const span = max - min;
  const avg = primary.length ? primary.reduce((s, v) => s + v, 0) / primary.length : NaN;

  const xAt = i => (rows.length <= 1 ? W / 2 : PX + (i / (rows.length - 1)) * (W - PX * 2));
  const yAt = v => (span === 0 ? H / 2 : PY + (1 - (v - min) / span) * (H - PY * 2));

  const lines = metric.series.map(s => ({
    ...s,
    pts: rows
      .map((r, i) => {
        const v = num(r[s.key]);
        return isNum(v) ? { x: xAt(i), y: yAt(v) } : null;
      })
      .filter(Boolean),
  }));

  const latestRow = [...rows].reverse().find(r => metric.series.some(s => isNum(num(r[s.key]))));
  const latestText = latestRow ? metric.series.map(s => fmt(latestRow[s.key], metric.decimals)).join('/') : '--';

  const hasTrend = trend && isNum(num(trend.delta));
  const arrow = trend?.direction === 'up' ? '▲' : trend?.direction === 'down' ? '▼' : '–';
  const deltaText = hasTrend ? `${num(trend.delta) > 0 ? '+' : ''}${fmt(trend.delta, metric.decimals)}` : '';

  return (
    <div style={{ border: '1px solid #EEF0F2', borderRadius: 12, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <div>
          <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: '#3A3A38' }}>{metric.label}</p>
          {hasTrend && (
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#888780' }}>
              {arrow} {deltaText} vs previous reading
            </p>
          )}
        </div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: metric.series[0].color, whiteSpace: 'nowrap' }}>
          {latestText} <span style={{ fontSize: 11, fontWeight: 600, color: '#888780' }}>{metric.unit}</span>
        </p>
      </div>

      {values.length === 0 ? (
        <p style={{ margin: '18px 0', fontSize: 12, color: '#888780', textAlign: 'center' }}>No readings in this period</p>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} role="img" aria-label={`${metric.label} over time`}>
            {[0, 0.5, 1].map(t => (
              <line
                key={t}
                x1={PX}
                x2={W - PX}
                y1={PY + t * (H - PY * 2)}
                y2={PY + t * (H - PY * 2)}
                stroke="#EEF0F2"
                strokeWidth="1"
              />
            ))}
            {lines.map(l => (
              <g key={l.key}>
                {l.pts.length > 1 && (
                  <polyline
                    points={l.pts.map(p => `${p.x},${p.y}`).join(' ')}
                    fill="none"
                    stroke={l.color}
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                )}
                {(rows.length <= 31 || l.pts.length === 1) &&
                  l.pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={l.color} />)}
              </g>
            ))}
          </svg>

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: '#888780', marginTop: 2 }}>
            <span>{shortDate(rows[0]?.date, withYear)}</span>
            <span>{shortDate(rows[rows.length - 1]?.date, withYear)}</span>
          </div>

          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#5F5E5A' }}>
            Range {fmt(min, metric.decimals)}–{fmt(max, metric.decimals)} · Avg {fmt(avg, metric.decimals)}
          </p>

          {metric.series.length > 1 && (
            <p style={{ margin: '4px 0 0', fontSize: 10.5, color: '#888780', display: 'flex', gap: 10 }}>
              {metric.series.map(s => (
                <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: s.color, display: 'inline-block' }} />
                  {s.label}
                </span>
              ))}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The popup itself
// ---------------------------------------------------------------------------

function HealthModal({ token, patientId, patientName, onClose }) {
  const [selection, setSelection] = useState({ id: '7d' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState(LIST_PAGE_SIZE);

  const range = useMemo(() => buildRange(selection), [selection]);
  const granularity = pickGranularity(range.totalDays);
  const withYear = range.totalDays > 120;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPatientHealth(token, patientId, range.days)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) { setError(e.message || 'Could not load health data'); setData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, patientId, range.days]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The API counts days back from today, so trim to the exact From / To here.
  const dailyRows = useMemo(
    () =>
      (data?.rows || [])
        .map(r => ({ ...r, date: String(r.date).slice(0, 10) }))
        .filter(r => r.date >= range.from && r.date <= range.to)
        .sort((a, b) => a.date.localeCompare(b.date)),
    [data, range.from, range.to]
  );

  // Charts use weekly / monthly averages for long ranges; the table stays daily.
  const chartRows = useMemo(() => aggregateRows(dailyRows, granularity), [dailyRows, granularity]);
  const tableRows = useMemo(() => dailyRows.slice().reverse(), [dailyRows]);

  // New range -> start the table from the first page again.
  useEffect(() => { setVisible(LIST_PAGE_SIZE); }, [dailyRows]);

  const summary = data?.summary || {};
  const trajectory = summary.trajectory || {};
  const positives = summary.positives || [];
  const watchouts = summary.watchouts || [];

  const daysWithData = dailyRows.filter(r => AGGREGATE_KEYS.some(k => isNum(num(r[k])))).length;
  const coveragePct = Math.min(100, Math.round((daysWithData / range.totalDays) * 100));

  const trajectoryCards = [
    { key: 'regulation', label: 'Regulation', value: trajectory.regulation },
    { key: 'repair', label: 'Recovery', value: trajectory.repair },
    { key: 'integration', label: 'Daily Integration', value: trajectory.integration },
  ];

  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#777', borderBottom: '1px solid #EEF0F2', position: 'sticky', top: 0, background: 'white', whiteSpace: 'nowrap' };
  const td = { padding: '7px 10px', fontSize: 12.5, borderBottom: '1px solid #F2F3F4', whiteSpace: 'nowrap' };

  const btn = { padding: '7px 14px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#0F4C75', fontSize: 12, fontWeight: 600, cursor: 'pointer' };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={e => { e.stopPropagation(); onClose(); }}
    >
      <style>{'@keyframes phb-spin { to { transform: rotate(360deg); } }'}</style>

      <div
        style={{
          background: 'white', borderRadius: 14, width: '100%', maxWidth: 880, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label={`Health data for ${patientName}`}
      >
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #EEF0F2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Health data — {patientName}</p>
            <p style={{ margin: '2px 0 0', fontSize: 11.5, color: '#888780' }}>
              Wearable readings synced from the patient's connected device
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 18, overflowY: 'auto' }}>
          <RangeSelector selection={selection} range={range} onChange={setSelection} />

          <p style={{ margin: '0 0 12px', fontSize: 12, color: '#888780' }}>
            {shortDate(range.from, true)} – {shortDate(range.to, true)} · {range.totalDays.toLocaleString()} days
            {granularity !== 'day' ? ` · charts show ${GRANULARITY_LABELS[granularity].toLowerCase()}` : ''}
          </p>

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '40px 0', color: '#888780', fontSize: 13 }}>
              <Loader2 size={16} style={{ animation: 'phb-spin 1s linear infinite' }} /> Loading health data…
            </div>
          ) : error ? (
            <p style={{ margin: 0, padding: '24px 0', textAlign: 'center', fontSize: 13, color: '#B3261E' }}>{error}</p>
          ) : dailyRows.length === 0 ? (
            <p style={{ margin: 0, padding: '32px 0', textAlign: 'center', fontSize: 13, color: '#888780' }}>
              No health data recorded for this patient in this date range.
            </p>
          ) : (
            <>
              {/* Overview */}
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#888780' }}>
                {daysWithData} of {range.totalDays.toLocaleString()} days with readings · {coveragePct}% data coverage
              </p>

              {summary.trajectory && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
                  {trajectoryCards.map(c => (
                    <div key={c.key} style={{ border: '1px solid #EEF0F2', borderRadius: 10, padding: 12 }}>
                      <p style={{ margin: 0, fontSize: 12, color: '#888780' }}>{c.label}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 15, fontWeight: 700, color: trajectoryColor(c.value) }}>
                        {trajectoryLabel(c.value)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {(positives.length > 0 || watchouts.length > 0) && (
                <div style={{ display: 'grid', gap: 7, marginBottom: 18 }}>
                  {positives.map((t, i) => (
                    <div key={`p-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <ShieldCheck size={15} color="#0F6E56" style={{ flexShrink: 0, marginTop: 1 }} />
                      <p style={{ margin: 0, fontSize: 13, color: '#3A3A38' }}>{t}</p>
                    </div>
                  ))}
                  {watchouts.map((t, i) => (
                    <div key={`w-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <AlertTriangle size={15} color="#B4780A" style={{ flexShrink: 0, marginTop: 1 }} />
                      <p style={{ margin: 0, fontSize: 13, color: '#3A3A38' }}>{t}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Charts */}
              <p style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 700 }}>
                Trends <span style={{ fontWeight: 400, color: '#888780' }}>· {GRANULARITY_LABELS[granularity]}</span>
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 20 }}>
                {METRICS.map(m => (
                  <MetricChart key={m.id} metric={m} rows={chartRows} trend={summary.trends?.[m.id]} withYear={withYear} />
                ))}
              </div>

              {/* Daily table */}
              <p style={{ margin: '0 0 8px', fontSize: 12.5, fontWeight: 700 }}>Daily readings</p>
              <div style={{ overflow: 'auto', maxHeight: 280, border: '1px solid #EEF0F2', borderRadius: 10, marginBottom: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th style={th}>Date</th>
                      <th style={th}>HR (bpm)</th>
                      <th style={th}>HRV (ms)</th>
                      <th style={th}>BP (mmHg)</th>
                      <th style={th}>Resp. (br/min)</th>
                      <th style={th}>Skin temp (°C)</th>
                      <th style={th}>Steps</th>
                      <th style={th}>Active energy (kcal)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.slice(0, visible).map(r => {
                      const hasBp = isNum(num(r.blood_pressure)) || isNum(num(r.blood_pressure_diastolic));
                      return (
                        <tr key={r.date}>
                          <td style={{ ...td, fontWeight: 600 }}>{shortDate(r.date, withYear)}</td>
                          <td style={td}>{fmt(r.heart_rate)}</td>
                          <td style={td}>{fmt(r.hrv)}</td>
                          <td style={td}>{hasBp ? `${fmt(r.blood_pressure)}/${fmt(r.blood_pressure_diastolic)}` : '--'}</td>
                          <td style={td}>{fmt(r.respiratory_rate, 1)}</td>
                          <td style={td}>{fmt(r.skin_temperature, 1)}</td>
                          <td style={td}>{fmt(r.movement)}</td>
                          <td style={td}>{fmt(r.active_energy)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {visible < tableRows.length && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', padding: '4px 0 12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#888780' }}>
                    Showing {Math.min(visible, tableRows.length).toLocaleString()} of {tableRows.length.toLocaleString()}
                  </span>
                  <button type="button" style={btn} onClick={() => setVisible(v => v + LIST_PAGE_SIZE)}>
                    Show {LIST_PAGE_SIZE} more
                  </button>
                  <button type="button" style={btn} onClick={() => setVisible(tableRows.length)}>
                    Show all
                  </button>
                </div>
              )}

              <p style={{ margin: 0, fontSize: 11.5, color: '#888780' }}>
                Wearable trends are informational and are not a medical diagnosis.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The trigger — rendered next to PatientReportsButton
// ---------------------------------------------------------------------------

const PatientHealthButton = ({ token, patientId, patientName }) => {
  const [open, setOpen] = useState(false);

  // A <span role="button"> rather than a <button>: it's often rendered
  // inside a parent <button> (the appointment list row), and a button
  // nested in a button is invalid HTML.
  function openModal(e) {
    e.stopPropagation();
    e.preventDefault();
    setOpen(true);
  }

  if (!patientId) return null;

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        title="View health data"
        aria-label={`View health data for ${patientName || 'patient'}`}
        onClick={openModal}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') openModal(e);
        }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24,
          borderRadius: 6, cursor: 'pointer', color: '#0F4C75', background: '#0F4C7514', flexShrink: 0,
        }}
      >
        <LineChart size={14} />
      </span>

      {open &&
        createPortal(
          <HealthModal
            token={token}
            patientId={patientId}
            patientName={patientName || 'Patient'}
            onClose={() => setOpen(false)}
          />,
          document.body
        )}
    </>
  );
};

export default PatientHealthButton;